import { makeStyles, tokens } from '@fluentui/react-components';
import { useRecordContext, useT } from '@powerportalspro/react';
import { EditorLabelPosition, RichEdit, richTextToPlainText } from '@powerportalspro/react-fluent';
import { useExampleDocument } from '../editors/use-example-document';

// A RichEdit opened on a formatted document, and beside it what the column would hold: the HTML
// Dynamics stores and reads back, and the line a grid shows for it. Both follow the editor as it
// is typed in. The record is a new one of the demo table, never saved.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.RichEditShowcase.RichEditShowcaseMainDemo';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  output: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  heading: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  // One line, cut off as a grid cell cuts it off.
  gridLine: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  html: {
    margin: 0,
    maxHeight: `calc(${tokens.lineHeightBase200} * 14)`,
    overflow: 'auto',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
});

export function RichEditShowcaseMainSample() {
  const styles = useStyles();
  const t = useT();
  useExampleDocument('ppp_richdescription', `${STRINGS_BASE}.showcase-document`);
  const { record } = useRecordContext();
  const html = (record?.properties?.ppp_richdescription as { value?: string | null } | undefined)?.value ?? '';

  return (
    <div className={styles.root}>
      <RichEdit
        columnName="ppp_richdescription"
        label={t(`${STRINGS_BASE}.editor-label`)}
        labelPosition={EditorLabelPosition.Above}
        rows={16}
      />

      <div className={styles.output}>
        <div className={styles.heading}>{t(`${STRINGS_BASE}.grid-title`)}</div>
        <div className={styles.gridLine}>{richTextToPlainText(html).replace(/\s*\n\s*/g, ' ')}</div>
      </div>

      <div className={styles.output}>
        <div className={styles.heading}>{t(`${STRINGS_BASE}.stored-title`)}</div>
        <pre className={styles.html}>{html}</pre>
      </div>
    </div>
  );
}
